import { DomainError } from "./errors";
import { decimalAmountCents, type MarketplaceOrderAction } from "./marketplace";

/**
 * Regras puras da integração com a Shopee (T1.17, RF-VEN-09; Open Platform v2). Valores chegam como número decimal do
 * JSON e viram centavos pela representação; datas chegam em segundos unix.
 */

/** Anúncios por página do `get_item_list` (uma página por mensagem da fila). */
export const SHOPEE_ITEMS_PAGE = 100;
/** Itens por chamada do `get_item_base_info` e pedidos por chamada do `get_order_detail`. */
export const SHOPEE_DETAIL_BATCH = 50;
/** Pedidos por página do `get_order_list` e páginas por rodada do polling. */
export const SHOPEE_POLL_PAGE = 100;
export const SHOPEE_POLL_MAX_PAGES = 3;
/** Janela máxima do `get_order_list` (a API recusa mais de 15 dias). */
export const SHOPEE_POLL_MAX_WINDOW_S = 15 * 86_400;
/** Folga sobre o último horário visto (atraso do índice) e primeira janela de uma loja nova. */
export const SHOPEE_POLL_OVERLAP_MS = 15 * 60_000;
export const SHOPEE_POLL_FIRST_MS = 24 * 3600_000;
/** Aviso de autorização da loja perto do fim. */
export const SHOPEE_AUTH_WARN_MS = 30 * 86_400_000;

/** Valor da Shopee em centavos (até 2 casas). */
export const shopeeAmountCents = (v: number | string): number => decimalAmountCents(v, "da Shopee");

const PAID = new Set([
  "READY_TO_SHIP",
  "PROCESSED",
  "RETRY_SHIP",
  "SHIPPED",
  "TO_CONFIRM_RECEIVE",
  "COMPLETED",
  // Brasil: pago, aguardando a nota fiscal do vendedor antes de "pronto para envio".
  "INVOICE_PENDING",
]);

/** O que fazer com o pedido conforme a situação na Shopee (padrão: só pago vira pedido de venda). */
export function shopeeOrderAction(status: string): MarketplaceOrderAction {
  if (PAID.has(status)) return "import";
  if (status === "UNPAID") return "wait";
  if (status === "CANCELLED") return "cancel";
  return "review";
}

export const SHOPEE_ORDER_STATUS_LABELS: Readonly<Record<string, string>> = {
  UNPAID: "Aguardando pagamento",
  INVOICE_PENDING: "Pago (aguardando nota fiscal)",
  READY_TO_SHIP: "Pronto para envio",
  PROCESSED: "Processado",
  RETRY_SHIP: "Reenvio",
  SHIPPED: "Enviado",
  TO_CONFIRM_RECEIVE: "Entregue (aguardando confirmação)",
  COMPLETED: "Concluído",
  IN_CANCEL: "Cancelamento solicitado",
  CANCELLED: "Cancelado",
  TO_RETURN: "Devolução",
};

const ORDER_SN_RE = /^[0-9A-Z]{6,32}$/;
/** `order_sn` da Shopee: letras maiúsculas e dígitos (vai na URL das chamadas). */
export const isShopeeOrderSn = (v: unknown): v is string => typeof v === "string" && ORDER_SN_RE.test(v);

/** Segundos unix → ISO (recusa zero, fração e datas absurdas). */
export function shopeeIsoFromUnix(sec: number): string {
  if (!Number.isInteger(sec) || sec <= 0 || sec > 9_999_999_999)
    throw new DomainError("invalid_date", `Data inválida da Shopee: ${String(sec)}`);
  return new Date(sec * 1000).toISOString();
}

/**
 * Janela (segundos unix) do polling por `update_time`: do último horário visto menos a folga (primeira vez: último
 * dia) até agora, nunca mais longa que os 15 dias que a API aceita.
 */
export function shopeePollWindow(lastSeenIso: string | null, now: number): { from: number; to: number } {
  const to = Math.floor(now / 1000);
  const seen = lastSeenIso ? Date.parse(lastSeenIso) : Number.NaN;
  const fromMs = Number.isNaN(seen) ? now - SHOPEE_POLL_FIRST_MS : seen - SHOPEE_POLL_OVERLAP_MS;
  return { from: Math.max(to - SHOPEE_POLL_MAX_WINDOW_S, Math.floor(fromMs / 1000)), to };
}

/**
 * Texto assinado das chamadas da Open Platform v2: `partner_id + caminho + timestamp` (públicas) e mais
 * `access_token + shop_id` nas chamadas da loja. A assinatura é o HMAC-SHA256 (hex) com a partner key.
 */
export function shopeeApiBaseString(
  partnerId: string,
  path: string,
  timestamp: number,
  shop?: { accessToken: string; shopId: string },
): string {
  return `${partnerId}${path}${timestamp}${shop ? `${shop.accessToken}${shop.shopId}` : ""}`;
}

/** Texto assinado do push: `<URL do callback>|<corpo cru>`. */
export const shopeePushBaseString = (url: string, rawBody: string): string => `${url}|${rawBody}`;

export type ShopeePush =
  | { kind: "order"; shopId: string; orderSn: string; status: string; updateTime: number }
  | { kind: "deauth"; shopId: string };

const shopIdOf = (v: unknown): string | null =>
  typeof v === "number" && Number.isSafeInteger(v) && v > 0
    ? String(v)
    : typeof v === "string" && /^\d{1,20}$/.test(v)
      ? v
      : null;

/**
 * Lê o push (já com a assinatura conferida). Só `3` (situação do pedido) e `2` (autorização cancelada pelo vendedor);
 * o resto, ou fora do formato, = null (ignorado). A situação é só dica: o pedido é buscado de novo na API.
 */
export function shopeePushOf(raw: unknown): ShopeePush | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as { code?: unknown; shop_id?: unknown; data?: unknown };
  const data = (b.data && typeof b.data === "object" ? b.data : {}) as Record<string, unknown>;
  if (b.code === 2) {
    const shopId = shopIdOf(b.shop_id) ?? shopIdOf(data.shop_id);
    return shopId ? { kind: "deauth", shopId } : null;
  }
  if (b.code !== 3) return null;
  const shopId = shopIdOf(b.shop_id);
  if (!shopId || !isShopeeOrderSn(data.ordersn)) return null;
  return {
    kind: "order",
    shopId,
    orderSn: data.ordersn,
    status: typeof data.status === "string" ? data.status.slice(0, 40) : "",
    updateTime: Number.isSafeInteger(data.update_time) ? (data.update_time as number) : 0,
  };
}

/** Autorização da loja vence em menos de 30 dias. */
export function shopeeAuthExpiringSoon(expiresAt: string | null, now: number = Date.now()): boolean {
  const ms = expiresAt ? Date.parse(expiresAt) : Number.NaN;
  return !Number.isNaN(ms) && ms - now < SHOPEE_AUTH_WARN_MS;
}
